import {z} from '@hono/zod-openapi';

import {ASSISTANT_AUTOMATION_RULE_ON_SCREEN_MAX_CHARACTERS} from '../constants/ASSISTANT_AUTOMATION_RULE_ON_SCREEN_MAX_CHARACTERS';

/**
 * The automation rule the user has on screen, as the page holds it now: the editor's candidate with
 * the user's unsaved changes, or the saved rule on its detail page. `ruleId` is null for a rule that
 * is not saved yet.
 *
 * Present only while such a page is mounted. Untrusted model input like the rest of the snapshot:
 * the backend judges every candidate again, so the slice grants nothing.
 *
 * `isEditable` is whether this user may change the rule now (the rule's `availableOperations.edit`).
 * A proposal is bound to the rule on screen only when it is editable. Absent means editable: clients
 * before contract 0.23.0 do not send it.
 *
 * `state` is the saved rule's state, absent for a new rule and from clients that do not send it.
 * The assistant reads it to tell a rule that is on (`active`) from one that is not.
 *
 * The configuration is sent whole or not at all. A cut JSON object would read as a different rule,
 * so a configuration above the cap fails the parse and the publisher leaves the slice out instead.
 */
export const assistantAutomationRuleOnScreenSchema = z
  .object({
    ruleId: z.string().uuid().nullable(),
    isEditable: z.boolean().optional(),
    state: z.enum(['draft', 'waiting_for_approval', 'active', 'paused', 'archived']).optional(),
    configuration: z
      .record(z.string(), z.unknown())
      .refine(
        (configuration) =>
          JSON.stringify(configuration).length <=
          ASSISTANT_AUTOMATION_RULE_ON_SCREEN_MAX_CHARACTERS,
        `Configuration too large (maximum ${ASSISTANT_AUTOMATION_RULE_ON_SCREEN_MAX_CHARACTERS} characters)`
      ),
  })
  .strict()
  .openapi('AssistantAutomationRuleOnScreen');

export type AssistantAutomationRuleOnScreen = z.infer<typeof assistantAutomationRuleOnScreenSchema>;
